import { FC } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Text, TextLink } from '@frontend/design-system';
import { PromoBanner } from '../../../common/components/promo-banners';
import { AUTO_ASSIGN_TASK_SETTINGS_BANNER_FF } from '../../constants';
import { useCallIntelInAppBannerVisibility } from '../../hooks/use-call-intel-in-app-banner-visibility';
import { getInAppAwarenessBannerTrackingId } from '../../tracking-ids';
import { InAppAwarenessPromoSource } from '../../types';

interface AutoAssignTaskSettingsBannerProps {
  styles?: Interpolation<Theme>;
  innerRef?: React.Ref<HTMLDivElement>;
  onDismiss?: () => void;
}

const COMPONENT_ID: InAppAwarenessPromoSource = 'call-intel-auto-assign-tasks';

export const AutoAssignTaskSettingsBanner: FC<AutoAssignTaskSettingsBannerProps> = ({
  styles,
  innerRef,
  onDismiss,
}) => {
  const { t } = useTranslation('highValueAdoption', { keyPrefix: 'call-intel' });
  const { selectedLocationIds } = useAppScopeStore();
  const { navigate: navigateToSettings } = useSettingsNavigate();

  const { ref, handleDismiss, handleInteraction, shouldShowBanner } = useCallIntelInAppBannerVisibility({
    locationIds: selectedLocationIds,
    componentId: COMPONENT_ID,
    enabled: true,
    maxVisibilityCount: 10,
    maxInteractionCount: 0,
    maxDismissedCount: 1,
    maxDaysSinceFirstSeen: 5,
    dismissExpiryDays: 1,
    bannerFF: AUTO_ASSIGN_TASK_SETTINGS_BANNER_FF,
    onDismiss,
  });

  const handleSettingsNavClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
    handleInteraction();
    navigateToSettings({
      to: '/personal/notifications/:type',
      params: { type: 'task-center' },
    });
  };

  const handleDismissClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    handleDismiss();
  };

  if (!shouldShowBanner) {
    return null;
  }

  return (
    <PromoBanner
      cardStyles={[styles, defaultStyles.banner]}
      innerRef={composeRefs(innerRef, ref) as React.Ref<HTMLDivElement>}
      badge={<Icon name='notification-small' size={16} css={defaultStyles.filledIcon} />}
      title={
        <Text size='small' textAlign='left'>
          <Trans t={t}>
            Manage Call Intelligence auto-assigned tasks notifications in{' '}
            <TextLink
              trackingId={getInAppAwarenessBannerTrackingId({
                source: COMPONENT_ID,
                action: 'personal-settings-link',
              })}
              onClick={handleSettingsNavClick}
              weight='bold'
            >
              Personal Settings
            </TextLink>
          </Trans>
        </Text>
      }
      dismissTrackingId={getInAppAwarenessBannerTrackingId({
        source: COMPONENT_ID,
        action: 'dismiss',
      })}
      onDismiss={handleDismissClick}
      color='primary'
      responsiveBadge={false}
      responsiveActions={false}
    />
  );
};

const defaultStyles = {
  banner: css`
    padding: ${theme.spacing(0.5, 1)};
  `,
  filledIcon: css`
    color: ${theme.colors.white};
    background-color: ${theme.colors.primary40};
    border-radius: ${theme.borderRadius.full};
    padding: ${theme.spacing(0.25)};
  `,
};
